<template>
  <div class="stc-root">

    <!-- Overlay de inicialização -->
    <div v-if="isStartingStream" class="stc-overlay">
      <div class="stc-overlay-box">
        <div class="stc-spin"></div>
        <p>Iniciando transmissão, aguarde...</p>
      </div>
    </div>

    <!-- ── Painel de controle (topo) ──────────────────────────── -->
    <div class="stc-control-panel">

      <!-- Status -->
      <div class="stc-status-block">
        <div class="stc-status-badge" :class="isStreaming ? 'is-live' : 'is-offline'">
          <span class="stc-status-dot"></span>
          {{ isStreaming ? 'AO VIVO' : 'OFFLINE' }}
        </div>
        <div v-if="isStreaming" class="stc-timer">
          <feather-icon icon="ClockIcon" size="14" />
          {{ streamTime }}
        </div>
      </div>

      <!-- Ações principais -->
      <div class="stc-actions">
        <span v-if="!isStreaming" class="stc-start-wrap" :id="!canStartStream ? 'stc-disabled-wrap' : null">
          <button
            class="stc-btn-start"
            :disabled="!canStartStream || isStartingStream"
            @click="startStream"
          >
            <feather-icon icon="PlayIcon" size="16" />
            Iniciar Transmissão
          </button>
        </span>

        <button
          v-else
          class="stc-btn-stop"
          @click="stopStream"
        >
          <feather-icon icon="SquareIcon" size="16" />
          Parar Transmissão
        </button>

        <button
          v-if="isStreaming && !showPreview && !loadingPreview"
          class="stc-btn-preview"
          @click="startPreviewCountdown"
        >
          <feather-icon icon="EyeIcon" size="14" />
          Ver Preview
        </button>

        <button
          v-if="isStreaming && showPreview"
          class="stc-btn-ghost"
          @click="showPreview = false"
        >
          <feather-icon icon="EyeOffIcon" size="14" />
          Ocultar Preview
        </button>
      </div>

      <!-- Aviso se não pode iniciar -->
      <p v-if="!canStartStream && !isStreaming" class="stc-warning">
        <feather-icon icon="AlertCircleIcon" size="13" />
        Preencha a configuração (stream key + vídeo) antes de iniciar.
      </p>
    </div>

    <!-- ── Conteúdo principal ─────────────────────────────────── -->
    <div class="stc-body">

      <!-- Coluna esquerda: logs + chat -->
      <div class="stc-left">

        <!-- Logs -->
        <div class="stc-card">
          <div class="stc-card-header">
            <feather-icon icon="TerminalIcon" size="14" />
            <span>Log da Transmissão</span>
          </div>
          <div class="stc-log-area">
            <div
              v-for="(log, i) in streamLogs"
              :key="i"
              class="stc-log-line"
              :class="'stc-log-' + log.type"
            >
              <span class="stc-log-ts">{{ log.time }}</span>
              <span class="stc-log-arrow">›</span>
              <span class="stc-log-msg">{{ log.message }}</span>
            </div>
            <div v-if="!streamLogs.length" class="stc-log-empty">
              Nenhum log ainda.
            </div>
          </div>
        </div>

        <!-- Envio de mensagem no chat (somente ao vivo) -->
        <div v-if="isStreaming" class="stc-card mt-3">
          <div class="stc-card-header">
            <feather-icon icon="SendIcon" size="14" />
            <span>Enviar no Chat</span>
          </div>
          <div class="stc-card-body">
            <form @submit.prevent="sendChatMessage" class="stc-chat-form">
              <textarea
                v-model="chatMessage"
                class="stc-textarea"
                placeholder="Digite sua mensagem..."
                rows="3"
                :disabled="sendingMessage"
              ></textarea>
              <button
                type="submit"
                class="stc-btn-send"
                :disabled="!chatMessage.trim() || sendingMessage"
              >
                <feather-icon icon="SendIcon" size="14" />
                {{ sendingMessage ? 'Enviando...' : 'Enviar' }}
              </button>
            </form>
          </div>
        </div>

      </div>

      <!-- Coluna direita: preview YouTube -->
      <div class="stc-right">

        <!-- Offline -->
        <div v-if="!isStreaming" class="stc-card stc-offline-card">
          <div class="stc-offline-icon">
            <feather-icon icon="EyeOffIcon" size="28" />
          </div>
          <p>Inicie a transmissão para ver o preview da live e o chat.</p>
        </div>

        <!-- Countdown do preview -->
        <div v-if="isStreaming && loadingPreview" class="stc-card stc-countdown-card">
          <div class="stc-spin stc-spin-lg"></div>
          <h5>Carregando Preview</h5>
          <p>Aguarde {{ previewCountdown }} segundo{{ previewCountdown !== 1 ? 's' : '' }}...</p>
          <button class="stc-btn-ghost" @click="cancelPreviewCountdown">
            <feather-icon icon="XIcon" size="13" /> Cancelar
          </button>
        </div>

        <!-- Aguardando clicar no preview -->
        <div v-if="isStreaming && !showPreview && !loadingPreview" class="stc-card stc-offline-card">
          <div class="stc-offline-icon">
            <feather-icon icon="RadioIcon" size="28" style="color:#28c76f" />
          </div>
          <p class="text-success" style="color:#28c76f; font-weight:600;">Transmissão em andamento!</p>
          <small>Clique em "Ver Preview" para exibir a live e o chat do YouTube.</small>
        </div>

        <!-- Preview ao vivo -->
        <div v-if="isStreaming && showPreview">

          <!-- Player YouTube -->
          <div v-if="streamId" class="stc-card stc-yt-card">
            <div class="stc-card-header">
              <feather-icon icon="YoutubeIcon" size="14" style="color:#ff0000" />
              <span>Live no YouTube</span>
            </div>
            <div class="stc-yt-player">
              <iframe
                :src="`https://www.youtube.com/embed/${streamId}?autoplay=1`"
                width="100%"
                height="300"
                frameborder="0"
                allowfullscreen
                allow="autoplay; encrypted-media"
                title="Live YouTube"
              />
            </div>
          </div>

          <!-- Chat YouTube -->
          <div class="stc-card stc-yt-card mt-3">
            <div class="stc-card-header">
              <feather-icon icon="MessageSquareIcon" size="14" />
              <span>Chat da Live</span>
            </div>
            <div class="stc-yt-chat">
              <iframe
                v-if="streamId"
                :src="`https://www.youtube.com/live_chat?v=${streamId}&embed_domain=${embedDomain}`"
                width="100%"
                height="400"
                frameborder="0"
                title="Chat da Live"
              />
              <div v-else class="stc-offline-card" style="height:120px;">
                <p style="color:#aab0bc; font-size:0.82rem;">Aguardando ID da transmissão...</p>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>

  </div>
</template>

<script>
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'

export default {
  name: 'StreamControls',
  props: {
    streamConfig:    { type: Object, required: true },
    isAuthenticated: { type: Boolean, default: false },
  },
  data() {
    return {
      isStreaming:      false,
      isStartingStream: false,
      streamTime:       '00:00:00',
      streamStartTime:  null,
      streamTimer:      null,
      streamLogs:       [],
      streamId:         null,
      embedDomain:      window.location.hostname,
      chatMessage:      '',
      sendingMessage:   false,
      showPreview:      false,
      loadingPreview:   false,
      previewCountdown: 10,
      countdownInterval: null,
    }
  },
  computed: {
    canStartStream() {
      const hasVideo     = this.streamConfig?.videoFile || this.streamConfig?.videoUrl
      const hasStreamKey = this.streamConfig?.streamKey
      return !!(hasStreamKey && hasVideo)
    },
  },
  watch: {
    isStreaming(val) {
      if (val) this.fetchBroadcastId()
    },
  },
  mounted() {
    this.checkStreamingStatus()
  },
  beforeDestroy() {
    this.stopTimer()
    this.cancelPreviewCountdown()
  },
  methods: {
    async startStream() {
      this.isStartingStream = true
      const userData = localStorage.getItem('userData')
      const userId   = userData ? JSON.parse(userData).id : null

      if (!userId) {
        this.$toast.error('Usuário não encontrado!')
        this.isStartingStream = false
        return
      }

      this.$toast.info('Iniciando live...')
      try {
        await youtubeService.createStreamWithVideo(userId)
        this.isStreaming     = true
        this.streamStartTime = new Date()
        this.startTimer()
        this.addLog('success', 'Transmissão iniciada com sucesso!')
        this.$emit('stream-status', { isStreaming: true })
      } catch (error) {
        this.addLog('error', `Erro ao iniciar: ${error.message || error}`)
        this.$toast.error(`Erro ao iniciar: ${error.message || error}`)
      } finally {
        this.isStartingStream = false
      }
    },

    async stopStream() {
      try {
        this.addLog('info', 'Parando transmissão...')
        const userData = localStorage.getItem('userData')
        const userId   = JSON.parse(userData).id
        await youtubeService.stopStream(userId)
        this.isStreaming = false
        this.showPreview = false
        this.stopTimer()
        this.addLog('success', 'Transmissão encerrada.')
        this.$emit('stream-status', { isStreaming: false })
      } catch (error) {
        if (error.code === 'LIVE_ALREADY_ENDED') {
          this.isStreaming = false
          this.showPreview = false
          this.stopTimer()
          this.addLog('warning', 'A transmissão já estava encerrada.')
          this.$toast.warning('A transmissão já estava encerrada.')
          this.$emit('stream-status', { isStreaming: false })
        } else {
          this.addLog('error', `Erro ao parar: ${error.message}`)
          this.$toast.error(`Erro ao parar: ${error.message}`)
        }
      }
    },

    async checkStreamingStatus() {
      try {
        const userData = localStorage.getItem('userData')
        const userId   = userData ? JSON.parse(userData).id : null
        if (!userId) return
        const response = await youtubeService.getStreamStatus(userId)
        if (response?.overall?.isStreaming) {
          this.isStreaming = true
          this.streamId   = response.youtube?.streamId || response.broadcastId || null
        } else {
          this.isStreaming = false
          this.streamId   = null
        }
      } catch {
        this.isStreaming = false
        this.streamId    = null
      }
    },

    async sendChatMessage() {
      if (!this.chatMessage.trim()) return
      this.sendingMessage = true
      try {
        await youtubeService.sendChatMessage(this.chatMessage.trim())
        this.addLog('success', `Mensagem enviada: "${this.chatMessage.trim()}"`)
        this.$toast.success('Mensagem enviada!')
        this.chatMessage = ''
      } catch (error) {
        this.addLog('error', `Erro ao enviar: ${error.message}`)
        this.$toast.error(`Erro: ${error.message}`)
      } finally {
        this.sendingMessage = false
      }
    },

    async fetchBroadcastId() {
      try {
        const userData = localStorage.getItem('userData')
        const userId   = userData ? JSON.parse(userData).id : null
        if (!userId) return
        const response = await youtubeService.getCurrentBroadcastId(userId)
        this.streamId = response?.broadcastId || response?.streamId || response?.videoId || null
      } catch (error) {
        this.addLog('error', `Erro ao buscar ID da live: ${error.message}`)
      }
    },

    startPreviewCountdown() {
      this.loadingPreview   = true
      this.previewCountdown = 10
      this.countdownInterval = setInterval(() => {
        this.previewCountdown -= 1
        if (this.previewCountdown <= 0) {
          clearInterval(this.countdownInterval)
          this.loadingPreview = false
          this.showPreview    = true
          this.addLog('success', 'Preview carregado!')
        }
      }, 1000)
    },

    cancelPreviewCountdown() {
      if (this.countdownInterval) clearInterval(this.countdownInterval)
      this.countdownInterval = null
      this.loadingPreview    = false
      this.previewCountdown  = 10
    },

    startTimer() {
      this.streamTimer = setInterval(() => {
        if (!this.streamStartTime) return
        const diff = Date.now() - this.streamStartTime.getTime()
        const h    = Math.floor(diff / 3600000)
        const m    = Math.floor((diff % 3600000) / 60000)
        const s    = Math.floor((diff % 60000) / 1000)
        this.streamTime = [h, m, s].map(n => String(n).padStart(2,'0')).join(':')
      }, 1000)
    },

    stopTimer() {
      if (this.streamTimer) { clearInterval(this.streamTimer); this.streamTimer = null }
      this.streamTime = '00:00:00'
    },

    addLog(type, message) {
      this.streamLogs.unshift({ type, message, time: new Date().toLocaleTimeString('pt-BR') })
      if (this.streamLogs.length > 50) this.streamLogs = this.streamLogs.slice(0, 50)
    },
  },
}
</script>

<style lang="scss" scoped>
/* ─── Root ──────────────────────────────────────────────────────── */
.stc-root { display: flex; flex-direction: column; gap: 16px; position: relative; }

/* ─── Overlay ───────────────────────────────────────────────────── */
.stc-overlay {
  position: fixed; inset: 0;
  background: rgba(255,255,255,0.88);
  z-index: 2000;
  display: flex; align-items: center; justify-content: center;
}
.stc-overlay-box {
  text-align: center; color: var(--dk-text-primary, #344563);
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  font-size: 0.9rem; font-weight: 500;
}

/* ─── Control panel ─────────────────────────────────────────────── */
.stc-control-panel {
  background: var(--dk-bg-card, #fff);
  border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 18px 22px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.stc-status-block { display: flex; align-items: center; gap: 12px; }
.stc-status-badge {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 14px; border-radius: 20px;
  font-size: 0.75rem; font-weight: 800; letter-spacing: 0.1em;
}
.stc-status-badge.is-live    { background: rgba(40,199,111,0.12); color: #28c76f; }
.stc-status-badge.is-offline { background: rgba(142,154,170,0.12); color: var(--dk-text-muted, #8e9aaa); }
.stc-status-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.is-live .stc-status-dot    { background: #28c76f; animation: stc-pulse 1.4s ease infinite; }
.is-offline .stc-status-dot { background: #8e9aaa; }
@keyframes stc-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(1.5); }
}

.stc-timer {
  display: flex; align-items: center; gap: 6px;
  font-size: 1.1rem; font-weight: 700;
  font-family: 'Menlo', 'Monaco', monospace;
  color: var(--dk-text-primary, #0D3349); letter-spacing: 0.05em;
}

.stc-actions { display: flex; align-items: center; gap: 10px; }

.stc-btn-start {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 22px; border: none; border-radius: 8px;
  background: #28c76f; color: #fff;
  font-size: 0.88rem; font-weight: 700; cursor: pointer;
  transition: background 0.15s;
}
.stc-btn-start:hover:not(:disabled) { background: #22b562; }
.stc-btn-start:disabled { opacity: 0.5; cursor: not-allowed; }

.stc-btn-stop {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 22px; border: none; border-radius: 8px;
  background: #C12B2B; color: #fff;
  font-size: 0.88rem; font-weight: 700; cursor: pointer;
  transition: background 0.15s;
}
.stc-btn-stop:hover { background: #a62424; }

.stc-btn-preview {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 16px;
  border: 1.5px solid #0D3349; border-radius: 8px;
  background: rgba(13,51,73,0.06); color: var(--dk-text-primary, #0D3349);
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
}
.stc-btn-preview:hover { background: rgba(13,51,73,0.12); }

.stc-btn-ghost {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 14px;
  border: 1.5px solid var(--dk-border, #e4e8ef); border-radius: 8px;
  background: var(--dk-bg-card, #fff); color: var(--dk-text-secondary, #5a6370);
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: border-color 0.15s;
}
.stc-btn-ghost:hover { border-color: #0D3349; color: var(--dk-text-primary, #0D3349); }

.stc-warning {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.77rem; color: var(--dk-text-muted, #8e9aaa);
  margin: 0; flex-basis: 100%;
}

/* ─── Body ──────────────────────────────────────────────────────── */
.stc-body { display: flex; gap: 18px; align-items: flex-start; }
.stc-left  { width: 280px; flex-shrink: 0; display: flex; flex-direction: column; }
.stc-right { flex: 1; min-width: 0; }

/* ─── Card ──────────────────────────────────────────────────────── */
.stc-card {
  background: var(--dk-bg-card, #fff);
  border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow: hidden;
}
.stc-card-header {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px;
  background: var(--dk-bg-page, #f8f9fb);
  border-bottom: 1.5px solid var(--dk-border-light, #f0f2f5);
  font-size: 0.82rem; font-weight: 700; color: var(--dk-text-primary, #0D3349);
}
.stc-card-body { padding: 14px 16px; }

/* ─── Logs ──────────────────────────────────────────────────────── */
.stc-log-area {
  padding: 10px 12px;
  height: 240px;
  overflow-y: auto;
  background: #0c1421;
  border-radius: 0 0 12px 12px;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
}
.stc-log-line {
  display: flex; align-items: baseline; gap: 6px;
  font-size: 0.72rem; padding: 2px 0; line-height: 1.5;
}
.stc-log-ts     { color: var(--dk-text-secondary, #4a6070); flex-shrink: 0; }
.stc-log-arrow  { color: var(--dk-text-secondary, #4a6070); flex-shrink: 0; }
.stc-log-msg    { color: var(--dk-text-faint, #b8c4cc); }
.stc-log-success .stc-log-msg { color: #28c76f; }
.stc-log-error  .stc-log-msg  { color: #e74c3c; }
.stc-log-warning .stc-log-msg { color: #F5B935; }
.stc-log-info .stc-log-msg    { color: #81b8d8; }
.stc-log-empty { color: var(--dk-text-secondary, #4a6070); font-size: 0.72rem; text-align: center; padding: 16px 0; }

/* ─── Chat form ─────────────────────────────────────────────────── */
.stc-chat-form { display: flex; flex-direction: column; gap: 10px; }
.stc-textarea {
  width: 100%; padding: 10px 12px;
  border: 1.5px solid var(--dk-border, #e4e8ef); border-radius: 8px;
  font-size: 0.83rem; color: var(--dk-text-primary, #344563);
  resize: none; outline: none;
  transition: border-color 0.15s;
}
.stc-textarea:focus { border-color: #0D3349; }
.stc-btn-send {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 16px; border: none; border-radius: 8px;
  background: #0D3349; color: #fff;
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
}
.stc-btn-send:hover:not(:disabled) { background: #0a2a3e; }
.stc-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }

/* ─── Offline / countdown cards ─────────────────────────────────── */
.stc-offline-card {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; text-align: center;
  padding: 36px 24px; gap: 10px;
  background: var(--dk-bg-card, #fff); border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
}
.stc-offline-icon { color: var(--dk-text-faint, #d0d7e3); margin-bottom: 4px; }
.stc-offline-card p     { font-size: 0.84rem; color: var(--dk-text-muted, #8e9aaa); margin: 0; }
.stc-offline-card small { font-size: 0.74rem; color: var(--dk-text-faint, #aab0bc); }

.stc-countdown-card {
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; padding: 36px 24px; text-align: center;
  background: var(--dk-bg-card, #fff); border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, #f0f2f5);
}
.stc-countdown-card h5  { font-size: 0.96rem; color: var(--dk-text-primary, #0D3349); margin: 0; }
.stc-countdown-card p   { font-size: 0.82rem; color: var(--dk-text-muted, #8e9aaa); margin: 0; }

/* ─── YouTube embeds ────────────────────────────────────────────── */
.stc-yt-card   { overflow: hidden; }
.stc-yt-player { line-height: 0; }
.stc-yt-chat   { line-height: 0; }

/* ─── Spinner ───────────────────────────────────────────────────── */
.stc-spin {
  width: 22px; height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(13,51,73,0.15);
  border-top-color: #0D3349;
  animation: stc-spin 0.75s linear infinite;
}
.stc-spin-lg { width: 38px; height: 38px; border-width: 3px; }
@keyframes stc-spin { to { transform: rotate(360deg); } }

.mt-3 { margin-top: 14px; }
</style>
